<template>
  <div class="monitor-docs">
    <div class="monitor-docs__head">
      <div>
        <div class="monitor-docs__title"><AppIcon name="menu_book" class="mr-1" /> Documentación de la API para Monitor</div>
        <small class="text-muted">Versión {{docs.version}} · actualizada {{docs.updated_at}}</small>
      </div>
      <div class="monitor-docs__actions">
        <b-button variant="outline-dark" size="sm" class="mr-1" @click="$emit('download-md')">
          <AppIcon name="download" class="mr-1" /> Descargar Markdown
        </b-button>
        <b-button variant="outline-dark" size="sm" @click="$emit('copy', JSON.stringify(docs, null, 2))">
          <AppIcon name="content_copy" class="mr-1" /> Copiar JSON
        </b-button>
      </div>
    </div>

    <b-row>
      <!-- índice -->
      <b-col lg="3" class="d-none d-lg-block">
        <nav class="monitor-docs__nav">
          <div class="monitor-docs__nav-group">General</div>
          <a v-for="item in generalIndex" :key="item.id" href="#" @click.prevent="go(item.id)">{{item.label}}</a>
          <div class="monitor-docs__nav-group">Endpoints</div>
          <a v-for="endpoint in docs.endpoints" :key="endpoint.id" href="#" @click.prevent="go(endpoint.id, true)" class="monitor-docs__nav-endpoint">
            <span class="monitor-docs__method-dot" :class="'monitor-docs__method-dot--' + endpoint.method.toLowerCase()">{{endpoint.method}}</span>
            {{endpoint.path}}
          </a>
          <div class="monitor-docs__nav-group">Formatos de reportes</div>
          <a v-for="format in docs.formats" :key="format.field" href="#" @click.prevent="go('format-' + format.field)">{{format.field}}</a>
          <a href="#" @click.prevent="go('docs-full-reports')">Reportes completos</a>
        </nav>
      </b-col>

      <!-- contenido -->
      <b-col lg="9">
        <section id="docs-intro" class="monitor-docs__section">
          <div class="monitor-docs__callout">
            <div class="mb-1"><b>URL base:</b> <code>{{docs.base_url}}</code></div>
            <ul class="mb-0">
              <li v-for="(line, index) in docs.intro" :key="'intro' + index">{{line}}</li>
            </ul>
          </div>
        </section>

        <section id="docs-auth" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="key" /> Autenticación</h5>
          <p>{{docs.auth.description}}</p>
          <div class="mb-2">
            <code v-for="header in docs.auth.headers" :key="header" class="monitor-docs__chip">{{header}}</code>
          </div>
          <MonitorCodeBlock :code="docs.auth.example" lang="bash" label="Ejemplo" />
        </section>

        <section id="docs-conventions" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="rule" /> Convenciones</h5>
          <ul class="monitor-docs__list">
            <li v-for="(line, index) in docs.conventions" :key="'conv' + index">{{line}}</li>
          </ul>
        </section>

        <section id="docs-flow" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="route" /> Flujo de alta y emparejamiento</h5>
          <div class="monitor-docs__flow">
            <div v-for="step in docs.flow" :key="step.step" class="monitor-docs__step">
              <span class="monitor-docs__step-number">{{step.step}}</span>
              <div>
                <b>{{step.title}}</b>
                <div class="text-muted">{{step.description}}</div>
              </div>
            </div>
          </div>
        </section>

        <section id="docs-guidelines" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="tips_and_updates" /> Recomendaciones de integración</h5>
          <ul class="monitor-docs__list monitor-docs__list--check">
            <li v-for="(line, index) in docs.guidelines" :key="'guide' + index">{{line}}</li>
          </ul>
        </section>

        <section id="docs-values" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="list" /> Valores posibles</h5>
          <b-row>
            <b-col v-for="group in docs.values" :key="group.title" md="6" class="mb-2">
              <div class="monitor-docs__subtitle">{{group.title}}</div>
              <b-table-simple small bordered class="mb-0 monitor-docs__table">
                <b-tbody>
                  <b-tr v-for="value in group.values" :key="value.value">
                    <b-td class="text-nowrap"><code>{{value.value}}</code></b-td>
                    <b-td>{{value.description}}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
          </b-row>
        </section>

        <section id="docs-wiped" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="delete_sweep" /> Qué se borra al emparejar</h5>
          <div v-for="group in docs.wiped_on_pair" :key="group.title" class="mb-2">
            <div><b>{{group.title}}:</b> <span class="text-muted">{{group.description}}</span></div>
            <code v-for="field in group.fields" :key="field" class="monitor-docs__chip">{{field}}</code>
          </div>
        </section>

        <section id="docs-endpoints" class="monitor-docs__section">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="monitor-docs__h mb-0"><AppIcon name="api" /> Endpoints</h5>
            <b-button variant="link" size="sm" @click="toggleAll">{{allOpen ? 'Contraer todos' : 'Expandir todos'}}</b-button>
          </div>
          <b-table-simple small hover class="monitor-docs__table monitor-docs__summary mt-2">
            <b-thead head-variant="light">
              <b-tr><b-th>Método</b-th><b-th>Ruta</b-th><b-th>Descripción</b-th></b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="endpoint in docs.endpoints" :key="'sum' + endpoint.id" class="monitor-docs__clickable" @click="go(endpoint.id, true)">
                <b-td><b-badge :variant="methodVariant(endpoint.method)">{{endpoint.method}}</b-badge></b-td>
                <b-td><code>{{endpoint.path}}</code></b-td>
                <b-td>{{endpoint.title}}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>

          <div v-for="endpoint in docs.endpoints" :key="endpoint.id" :id="endpoint.id" class="monitor-docs__endpoint">
            <div class="monitor-docs__endpoint-head" @click="toggle(endpoint.id)">
              <b-badge :variant="methodVariant(endpoint.method)" class="monitor-docs__method">{{endpoint.method}}</b-badge>
              <code class="monitor-docs__path">{{endpoint.path}}</code>
              <span class="monitor-docs__endpoint-title">{{endpoint.title}}</span>
              <AppIcon :name="open[endpoint.id] ? 'expand_less' : 'expand_more'" class="ml-auto" />
            </div>
            <b-collapse :visible="!!open[endpoint.id]">
              <div class="monitor-docs__endpoint-body">
                <p>{{endpoint.description}}</p>

                <div v-if="endpoint.body.length">
                  <div class="monitor-docs__subtitle">Campos del body</div>
                  <b-table-simple small bordered responsive class="monitor-docs__table">
                    <b-thead head-variant="light">
                      <b-tr><b-th>Campo</b-th><b-th>Tipo</b-th><b-th>Obligatorio</b-th><b-th>Descripción</b-th></b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr v-for="field in endpoint.body" :key="field.field">
                        <b-td class="text-nowrap"><code>{{field.field}}</code></b-td>
                        <b-td class="text-nowrap">{{field.type}}</b-td>
                        <b-td><b-badge :variant="field.required == 'Sí' ? 'dark' : 'light'">{{field.required}}</b-badge></b-td>
                        <b-td>{{field.description}}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </div>

                <b-tabs small>
                  <b-tab title="Request" active>
                    <div class="pt-2">
                      <MonitorCodeBlock :code="endpoint.curl" lang="bash" label="curl" />
                      <MonitorCodeBlock v-if="endpoint.request" :code="endpoint.request" lang="json" label="Body JSON" />
                    </div>
                  </b-tab>
                  <b-tab title="Respuesta 200">
                    <div class="pt-2">
                      <MonitorCodeBlock :code="endpoint.response" lang="json" />
                    </div>
                  </b-tab>
                  <b-tab v-if="endpoint.errors.length" :title="'Errores (' + endpoint.errors.length + ')'">
                    <div class="pt-2">
                      <b-table-simple small bordered responsive class="monitor-docs__table">
                        <b-thead head-variant="light">
                          <b-tr><b-th>Código</b-th><b-th>Cuándo</b-th><b-th>Mensaje</b-th></b-tr>
                        </b-thead>
                        <b-tbody>
                          <b-tr v-for="(error, index) in endpoint.errors" :key="index">
                            <b-td><b-badge :variant="error.status >= 500 ? 'danger' : (error.status == 409 ? 'warning' : 'secondary')">{{error.status}}</b-badge></b-td>
                            <b-td>{{error.when}}</b-td>
                            <b-td><small>{{error.message}}</small></b-td>
                          </b-tr>
                        </b-tbody>
                      </b-table-simple>
                      <MonitorCodeBlock :code="endpoint.errors[0].body" lang="json" :label="'Ejemplo ' + endpoint.errors[0].status" />
                    </div>
                  </b-tab>
                </b-tabs>
              </div>
            </b-collapse>
          </div>
        </section>

        <section id="docs-formats" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="data_object" /> Formatos de los reportes</h5>
          <div v-for="format in docs.formats" :key="format.field" :id="'format-' + format.field" class="monitor-docs__format">
            <div class="mb-1">
              <code class="monitor-docs__format-name">{{format.field}}</code>
              <b-badge v-for="target in format.applies_to" :key="target" variant="info" class="ml-1">{{target}}</b-badge>
            </div>
            <p class="mb-2">{{format.description}}</p>
            <ul class="monitor-docs__list">
              <li v-for="(rule, index) in format.rules" :key="index">{{rule}}</li>
            </ul>
            <MonitorCodeBlock v-if="format.example" :code="format.example" :lang="format.example_lang || 'json'" label="Ejemplo" />
          </div>
        </section>

        <section id="docs-full-reports" class="monitor-docs__section">
          <h5 class="monitor-docs__h"><AppIcon name="description" /> Reportes completos</h5>
          <div v-for="report in docs.full_reports" :key="report.title" class="mb-3">
            <div class="mb-1"><b>{{report.title}}</b> <b-badge variant="primary" class="ml-1">POST</b-badge> <code>{{report.path}}</code></div>
            <MonitorCodeBlock :code="report.body" lang="json" label="Body JSON" />
          </div>
        </section>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import MonitorCodeBlock from './monitorCodeBlock'

  export default {
    components: {
      MonitorCodeBlock,
    },
    props: {
      docs: { type: Object, required: true },
    },
    data: () => {
      return {
        open: {},
        generalIndex: [
          { id: 'docs-intro', label: 'Introducción' },
          { id: 'docs-auth', label: 'Autenticación' },
          { id: 'docs-conventions', label: 'Convenciones' },
          { id: 'docs-flow', label: 'Flujo de alta y emparejamiento' },
          { id: 'docs-guidelines', label: 'Recomendaciones' },
          { id: 'docs-values', label: 'Valores posibles' },
          { id: 'docs-wiped', label: 'Qué se borra al emparejar' },
          { id: 'docs-endpoints', label: 'Resumen de endpoints' },
        ],
      }
    },
    computed: {
      allOpen() {
        return this.docs.endpoints.every(endpoint => this.open[endpoint.id])
      },
    },
    methods: {
      methodVariant(method) {
        return method == 'GET' ? 'success' : 'primary'
      },
      toggle(id) {
        this.$set(this.open, id, !this.open[id])
      },
      toggleAll() {
        var value = !this.allOpen
        this.docs.endpoints.forEach(endpoint => this.$set(this.open, endpoint.id, value))
      },
      go(id, expand) {
        if(expand) this.$set(this.open, id, true)
        this.$nextTick(() => {
          var element = document.getElementById(id)
          if(element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      },
    }
  }
</script>

<style scoped>
  .monitor-docs__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
  }

  .monitor-docs__title {
    font-weight: 600;
    font-size: 16px;
  }

  .monitor-docs__nav {
    position: sticky;
    top: 70px;
    max-height: calc(100vh - 90px);
    overflow-y: auto;
    border-right: 1px solid #e9ecef;
    padding-right: 8px;
    font-size: 13px;
  }

  .monitor-docs__nav a {
    display: block;
    padding: 3px 6px;
    color: #343a40;
    border-radius: 4px;
    text-decoration: none;
    word-break: break-all;
  }

  .monitor-docs__nav a:hover {
    background: #f1f3f5;
  }

  .monitor-docs__nav-group {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: #868e96;
    font-weight: 700;
    margin: 12px 0 4px 6px;
  }

  .monitor-docs__nav-group:first-child {
    margin-top: 0;
  }

  .monitor-docs__method-dot {
    display: inline-block;
    min-width: 34px;
    font-size: 10px;
    font-weight: 700;
  }

  .monitor-docs__method-dot--get { color: #28a745; }
  .monitor-docs__method-dot--post { color: #007bff; }

  .monitor-docs__section {
    padding-bottom: 18px;
    margin-bottom: 18px;
    border-bottom: 1px solid #f1f3f5;
    scroll-margin-top: 80px;
  }

  .monitor-docs__h {
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .monitor-docs__subtitle {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .monitor-docs__callout {
    background: #f4f8ff;
    border-left: 4px solid #007bff;
    border-radius: 4px;
    padding: 10px 14px;
  }

  .monitor-docs__callout ul {
    padding-left: 18px;
  }

  .monitor-docs__list {
    padding-left: 18px;
    margin-bottom: 8px;
  }

  .monitor-docs__list li {
    margin-bottom: 3px;
  }

  .monitor-docs__list--check {
    list-style: none;
    padding-left: 0;
  }

  .monitor-docs__list--check li {
    position: relative;
    padding-left: 22px;
  }

  .monitor-docs__list--check li::before {
    content: '✓';
    position: absolute;
    left: 4px;
    color: #28a745;
    font-weight: 700;
  }

  .monitor-docs__chip {
    display: inline-block;
    background: #f1f3f5;
    border-radius: 4px;
    padding: 1px 6px;
    margin: 2px 4px 2px 0;
    font-size: 12px;
  }

  .monitor-docs__flow {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
  }

  .monitor-docs__step {
    display: flex;
    gap: 10px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 10px;
  }

  .monitor-docs__step-number {
    flex: 0 0 26px;
    height: 26px;
    border-radius: 50%;
    background: #343a40;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
  }

  .monitor-docs__table {
    font-size: 13px;
  }

  .monitor-docs__clickable {
    cursor: pointer;
  }

  .monitor-docs__endpoint {
    border: 1px solid #e3e6ea;
    border-radius: 6px;
    margin-bottom: 10px;
    scroll-margin-top: 80px;
  }

  .monitor-docs__endpoint-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    background: #fafbfc;
    border-radius: 6px;
  }

  .monitor-docs__endpoint-head:hover {
    background: #f1f3f5;
  }

  .monitor-docs__method {
    min-width: 46px;
  }

  .monitor-docs__path {
    font-size: 13px;
    word-break: break-all;
  }

  .monitor-docs__endpoint-title {
    color: #6c757d;
    font-size: 13px;
  }

  .monitor-docs__endpoint-body {
    padding: 10px 12px 4px;
    border-top: 1px solid #e3e6ea;
  }

  .monitor-docs__format {
    border-left: 3px solid #17a2b8;
    padding: 4px 0 4px 12px;
    margin-bottom: 18px;
    scroll-margin-top: 80px;
  }

  .monitor-docs__format-name {
    font-size: 15px;
    font-weight: 600;
  }

  @media (max-width: 767px) {
    .monitor-docs__endpoint-title {
      display: none;
    }
  }
</style>
